import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { AiAgentResponse } from '../../../models/responses/ai-agent-response';
import { AiConfigurationPageEventsService } from '../../../events/ai-configuration-page-events.service';
import { OutboundCampaignService } from '../../../services/outbound-campaign.service';
import { OutboundCampaignResponse } from '../../../models/responses/outbound-campaign-response';
import { FUNCTIONAL_ERROR, getErrorType, handleErrors } from '../../../utilities/error-handler-utils';
import moment from 'moment-timezone';
import { timezoneValidator } from '../../../validators/timezone-validator';
import { map, Observable,startWith } from 'rxjs';

@Component({
  selector: 'app-add-edit-campaign-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatDivider,
    MatIconModule,
    NgIf,
    MatAutocompleteModule,
    AsyncPipe,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    NgxMatTimepickerModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './add-edit-campaign-dialog.component.html',
  styleUrl: './add-edit-campaign-dialog.component.scss'
})
export class AddEditCampaignDialogComponent {
  isEditing: boolean = false;
  isLoading: boolean = false;
  fileSet: boolean = false;
  file_store!: FileList;
  file_list: Array<string> = [];
  errorMessage: string | undefined = undefined;
  outboundCampaignForm!: FormGroup;
  filteredOptions!: Observable<string[]>;
  timeZones: string[] = moment.tz.names();

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AddEditCampaignDialogComponent>,
    private outboundCampaignService: OutboundCampaignService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: { campaign: OutboundCampaignResponse, aiAgent: AiAgentResponse },
    private aiConfigurationPageEventsService: AiConfigurationPageEventsService
  ) { }

  ngOnInit() {
    this.initCampaignForm();

  }

  initCampaignForm() {
    if (this.data.campaign) {
      this.isEditing = true;
    }


    let { date, time } = this.getOptionalCampaignDateAndTime();

    this.outboundCampaignForm = this.formBuilder.group({
      id: [this.data.campaign ? this.data.campaign : undefined],
      date: [date ? date : '', Validators.required],
      time: [time ? time : '', Validators.required],
      timezone: ['', [Validators.required, timezoneValidator(this.timeZones)]],
      campaignName: [this.data.campaign?.name ? this.data.campaign.name : '', Validators.required],
    });

    this.filteredOptions = this.outboundCampaignForm.controls['timezone'].valueChanges.pipe(
          startWith(''),
          map(value => this._filter(value || '')),
        );
  }

  onSubmitForm() {
    const formData = this.getFileData();
    if (this.outboundCampaignForm.valid && formData) {
      this.isLoading = true;
      const formValues = this.outboundCampaignForm.value;
      const localizedDate = formValues['date'] as Date;
      const time = this.getHoursAndDate(formValues['time']);
      const date = this.getUtcDate(localizedDate, time.hour, time.minute, formValues['timezone']);

      formData.append('name', formValues['campaignName'])

      this.outboundCampaignService.scheduleBatch(this.data.aiAgent.id, date, formData).subscribe({
        next: () => {
          this.isLoading = false;
          this.snackBar.open('Campaign was scheduled successfully', 'Close', { duration: 5000 });
          this.aiConfigurationPageEventsService.onRefreshAiAgentEvent.emit();
          this.dialogRef.close()
        },
        error: (error) => {
          this.errorMessage = handleErrors(error);
          this.isLoading = false;
        }
      })
    }
  }



  handleFileInputChange(l: FileList) {
    this.file_store = l;
    this.fileSet = this.file_store.length > 0;
  }

  private getUtcDate(date: Date, hours: number, minutes: number, timezone: string) {
    debugger;
    let localizedDate = moment.tz({
      year: date.getFullYear(),
      month: date.getMonth(), // Note: Months are 0-indexed (0 = January, 1 = February, etc.)
      day: date.getDate(),
      hour: hours,
      minute: minutes
    }, timezone);

    return localizedDate.toDate()
  }

  private getFileData() {
    if (this.file_store.length > 0) {
      let fd = new FormData();
      fd.append("file", this.file_store[0], this.file_store[0].name);
      return fd;
    }

    return undefined;
  }


  private getHoursAndDate(timeString: String) {
    const timeParts = timeString.split(/[: ]/)

    let hour = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);
    const isAfternoon = timeParts[2].toLowerCase().includes('pm');

    if (isAfternoon && hour != 12) {
      hour += 12;
    } else if (!isAfternoon && hour == 12) {
      hour -= 12;
    }

    return { hour: hour, minute: minutes }
  }


  private getOptionalCampaignDateAndTime() {
    let date = undefined;
    let time = undefined;
    if (this.data.campaign) {
      const day = this.data.campaign.executionTime.getDate();
      const month = this.data.campaign.executionTime.getMonth() + 1;
      const year = this.data.campaign.executionTime.getFullYear();
      const hours = this.data.campaign.executionTime.getHours();
      const minutes = this.data.campaign.executionTime.getHours();

      time = `${hours}:${minutes}`;
      date = `${month}/${day}/${year}`;
    }
    return { date, time };
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.timeZones.filter(option => option.toLowerCase().includes(filterValue));
  }

}
